import { useEffect, useState } from 'react'
import { api } from '../../lib/api.js'

/**
 * The per-body verification rollup, live.
 *
 * ⚠️ WHY THE CARDS NEED THIS AND CANNOT DERIVE IT. "Has anyone checked this
 * body against its own site" is not a property of the championship object —
 * it lives in the stamp store, and the object says nothing about it. Measured
 * 2026-10-16T01:49Z: 15,854 body stamps, 13,461 `inferred`, 2,393
 * `unverified`, and **zero verified**, across 718 bodies all carrying the flag
 * "body not verified against its own site". Not one body in the estate has
 * ever been confirmed, and until this hook existed no card could say so.
 *
 * ⚠️ UNREACHABLE IS REPORTED, NEVER TREATED AS VERIFIED. If the route fails,
 * `error` is set and the cards show nothing rather than a clean bill — an
 * absent flag reads as "checked and fine", which is the exact inversion this
 * whole lane is about.
 *
 * ⚠️ AND A BODY WITH NO ROW IS NOT A BODY WITH NOTHING WRONG. The route lists
 * every body holding at least one stamp, by construction; a body with no
 * stamps at all has no row. `byKey.get()` returning undefined therefore means
 * UNKNOWN, and `cardIssues` only raises the flag when a row exists and says
 * verified === 0.
 */
export function useVerificationBodies() {
  const [byKey, setByKey] = useState(null)
  const [meta, setMeta] = useState(null)
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState('')

  /* ⚠️ THE ESTATE-WIDE SPLIT IS READ TOO, AND IT IS THE HEADLINE. The per-body
   * rollup can only list bodies that hold a stamp; the summary is the one
   * figure that reconciles to the whole object, carries its own denominator
   * with the timestamp it was pulled, and says `reconciles: true/false`. A
   * surface that showed only the per-body rows would be quietly reporting a
   * subset as a total. */
  useEffect(() => {
    const ac = new AbortController()
    let live = true
    api.verificationSummary({ signal: ac.signal })
      .then((d) => { if (live) setSummary(d || null) })
      .catch(() => { /* the per-body read below carries the error message */ })
    return () => { live = false; ac.abort() }
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    let live = true
    api.verificationBodies({ signal: ac.signal })
      .then((d) => {
        if (!live) return
        const m = new Map()
        for (const r of d?.rows || []) {
          const b = r.body || {}
          m.set(String(r.bodyKey || '').toLowerCase(), {
            bodyKey: r.bodyKey,
            stamped: r.stamped || 0,
            verified: b.verified || 0,
            inferred: b.inferred || 0,
            unverified: b.unverified || 0,
            flag: r.flag || null,
          })
        }
        setByKey(m)
        setMeta({ bodies: d?.bodies || 0, capped: d?.capped === true, generatedAt: d?.generatedAt || null })
      })
      .catch((e) => { if (live && e?.name !== 'AbortError') setError(e?.message || 'Could not read the verification rollup.') })
    return () => { live = false; ac.abort() }
  }, [])

  return { byKey, meta, summary, error }
}

/**
 * Find a body's row. A card is keyed on a registry key (`AL-AHSAA`); the stamp
 * store is keyed on the QUALIFIED governing-body string the records carry
 * (`AHSAA (public)`). Neither is a substring test — `ACAA` and `ACAA
 * (Christian)` are two organisations, and the stripped form is what put 27
 * Michigan championships on Mississippi's card.
 */
export function verificationFor(byKey, entity, rows = []) {
  if (!byKey || !entity) return null
  const tried = new Set()
  const take = (k) => {
    const s = String(k || '').trim().toLowerCase()
    if (!s || tried.has(s)) return null
    tried.add(s)
    return byKey.get(s) || null
  }
  /* The strings the records themselves carry come FIRST: they are what the
   * store is keyed on, so they are the only exact answer. */
  const labels = new Map()
  for (const c of rows) {
    const v = String((c?.raw || c || {}).sr_conference || '').trim()
    if (v) labels.set(v, (labels.get(v) || 0) + 1)
  }
  for (const [lab] of [...labels.entries()].sort((a, b) => b[1] - a[1])) {
    const hit = take(lab)
    if (hit) return hit
  }
  return take(entity.name) || take(entity.acronym) || null
}
